import { createAccessFetch } from '../auth/access';
import { analytics, AnalyticsIdentity, newId } from './client';
import { analyticsDiagnostics } from './diagnostics';
import { captureExecution, BrowserExecutionSnapshot, RequestAnalyticsContext } from './context';
import { ANALYTICS_CONTEXT_HEADER, encodeAttributionContext } from './contract-v2';
import {
  AnalyticsOperation,
  HEADERS,
  operationFor,
  SearchPhase,
  searchProperties,
} from './contract';

interface RequestAnalytics {
  identity: AnalyticsIdentity;
  operationId: string;
  operation?: AnalyticsOperation;
  searchId?: string;
  phase?: SearchPhase;
  origin: string;
  execution?: BrowserExecutionSnapshot;
}
const counts = new Map<
  string,
  { identity: AnalyticsIdentity; searchId: string; created: number }
>();
const searchIdentities = new Map<string, AnalyticsIdentity>();
const responseSearchIds = new WeakMap<object, string>();
const responseExecutions = new WeakMap<object, BrowserExecutionSnapshot>();
export function executionForResponse(result: unknown): BrowserExecutionSnapshot | undefined {
  return result && typeof result === 'object' ? responseExecutions.get(result) : undefined;
}
export function searchIdForResponse(result: unknown): string | undefined {
  return result && typeof result === 'object' ? responseSearchIds.get(result) : undefined;
}
const boundedSet = <T>(map: Map<string, T>, key: string, value: T) => {
  if (map.size >= 200) map.delete(map.keys().next().value!);
  map.set(key, value);
};

export function isApplicationRequest(url: string, baseURL: string): boolean {
  try {
    return new URL(url, baseURL).origin === new URL(baseURL).origin;
  } catch {
    return false;
  }
}

function bodyObject(value: unknown): Record<string, any> {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return value && Object.getPrototypeOf(value) === Object.prototype
    ? (value as Record<string, any>)
    : {};
}

const attributionHeaders = new Set([...Object.values(HEADERS), ANALYTICS_CONTEXT_HEADER].map((name) => name.toLowerCase()));
function headerObject(input: any): Record<string, any> {
  if (typeof Headers !== 'undefined' && input instanceof Headers) return Object.fromEntries(input.entries());
  return typeof input?.toJSON === 'function' ? input.toJSON() : input || {};
}
function cleanHeaders(input: any): Record<string, string> {
  const source = headerObject(input);
  return Object.fromEntries(Object.entries(source).filter(([key]) => !attributionHeaders.has(key.toLowerCase()))) as Record<string, string>;
}
function headerValue(input: any, name: string): unknown {
  const source = headerObject(input);
  return Object.entries(source).find(([key]) => key.toLowerCase() === name.toLowerCase())?.[1];
}
function stripAttribution(config: any): any {
  const headers = cleanHeaders(config.headers);
  const sourceHeaders = headerObject(config.headers);
  if (!config.__analytics && !config.analyticsContext && !config.analyticsProperties &&
    Object.keys(headers).length === Object.keys(sourceHeaders).length) return config;
  const { __analytics, analyticsContext, analyticsProperties, ...rest } = config;
  return { ...rest, ...(config.headers ? { headers } : {}) };
}
function sameActor(a: AnalyticsIdentity, b: AnalyticsIdentity | undefined): boolean {
  return !!b && a.userId === b.userId && a.deviceId === b.deviceId &&
    (a.clientId ?? null) === (b.clientId ?? null) && a.accountEpoch === b.accountEpoch;
}

/** Caller context is transport metadata and never modifies business criteria. */
function prepareExplicitRequest(config: any, apiBase: string, input: RequestAnalyticsContext): any {
  const execution = captureExecution(input);
  if (!execution?.deviceId || !execution.sessionId || !execution.operationId || execution.excluded) {
    analyticsDiagnostics.increment('missing_context');
    return stripAttribution(config);
  }
  const encoded = encodeAttributionContext(execution.attribution);
  if (!encoded) {
    analyticsDiagnostics.increment('invalid_context');
    return stripAttribution(config);
  }
  const url = new URL(config.url || '', config.baseURL || apiBase);
  const operation = operationFor(config.method || 'GET', url.pathname);
  const identity: AnalyticsIdentity = {
    userId: execution.userId, deviceId: execution.deviceId, sessionId: execution.sessionId,
    clientId: execution.clientId, accountEpoch: execution.accountEpoch, lastActivity: execution.capturedAt,
  };
  const metadata: RequestAnalytics = {
    identity, operationId: execution.operationId, operation,
    searchId: execution.searchId, phase: execution.phase, origin: url.origin, execution,
  };
  const headers = {
    ...cleanHeaders(config.headers),
    [HEADERS.device]: identity.deviceId,
    [HEADERS.session]: String(identity.sessionId),
    [HEADERS.operation]: metadata.operationId,
    [ANALYTICS_CONTEXT_HEADER]: encoded,
    ...(metadata.searchId ? { [HEADERS.search]: metadata.searchId } : {}),
    ...(metadata.phase ? { [HEADERS.phase]: metadata.phase } : {}),
  };
  if (operation?.kind === 'search' && execution.phase === 'submit' && input.search) {
    const search = input.search;
    if (search.submitted_criteria) {
      analytics.trackSemantic('search_submitted', {
        ...execution.attribution, search_id: search.search_id, operation_id: execution.operationId,
      }, {
        surface: search.surface, search_id: search.search_id,
        trigger: execution.attribution.trigger || search.trigger,
        submitted_criteria: search.submitted_criteria, search_type: operation.name,
        search_phase: 'submit',
      }, { ...identity, factKey: `${search.search_id}:submitted`, executionKind: execution.executionKind });
    } else analyticsDiagnostics.increment('missing_context');
  }
  if (operation?.kind === 'chat') {
    const body = bodyObject(config.data);
    analytics.trackSemantic('agent_prompt_submitted', execution.attribution, {
      operation_id: execution.operationId,
      conversation_id: typeof body.sessionId === 'string' ? body.sessionId : undefined,
      prompt_length: typeof body.message === 'string' ? body.message.length : undefined,
    }, { ...identity, factKey: execution.operationId, executionKind: execution.executionKind });
  }
  return { ...config, headers, __analytics: metadata };
}

export function prepareRequest(config: any, apiBase: string): any {
  try {
    const url = new URL(config.url || '', config.baseURL || apiBase);
    if (!isApplicationRequest(url.href, apiBase)) return stripAttribution(config);
    if (!analytics.enabled) return stripAttribution(config);
    // Retried requests retain the original event ID and do not repeat submission.
    if (config.__analytics) {
      if (sameActor(config.__analytics.identity, analytics.snapshot())) return config;
      analyticsDiagnostics.increment('stale_context');
      return stripAttribution(config);
    }
    if (config.analyticsContext) return prepareExplicitRequest(config, apiBase, config.analyticsContext);
    const method = (config.method || 'GET').toUpperCase();
    const operation = operationFor(method, url.pathname);
    if (operation?.kind === 'search' || operation?.kind === 'chat') analytics.touch();
    const identity = analytics.snapshot();
    if (!identity) return config;
    const data = bodyObject(method === 'GET' ? config.params : config.data);
    const metadata: RequestAnalytics = {
      identity,
      operation,
      operationId: newId(),
      origin: url.origin,
    };
    const headers = cleanHeaders(config.headers);
    if (operation?.kind === 'search' || operation?.kind === 'count') {
      analyticsDiagnostics.increment('legacy_fallback');
      const owner = `${identity.userId || 'anonymous'}:${identity.clientId || ''}:${identity.accountEpoch || ''}:${identity.deviceId}:${url.origin}:${
        operation.name
      }`;
      const countKey = typeof data.countKey === 'string' ? data.countKey : undefined;
      const knownCount = countKey ? counts.get(`${owner}:${countKey}`) : undefined;
      const explicitSearch = headerValue(config.headers, HEADERS.search);
      const explicitPhase = headerValue(config.headers, HEADERS.phase);
      const isPage =
        explicitPhase === 'page' ||
        Number(data.skip || data.cursor) > 0 ||
        Number(data.current || data.page) > 1 ||
        (!!countKey && operation.kind !== 'count');
      metadata.phase = operation.kind === 'count' ? 'count' : isPage ? 'page' : 'submit';
      metadata.searchId =
        typeof explicitSearch === 'string'
          ? explicitSearch
          : metadata.phase === 'submit'
          ? newId()
          : knownCount?.searchId || newId();
      if (metadata.phase !== 'submit' && !knownCount && !explicitSearch) analyticsDiagnostics.increment('missing_context');
      // A deferred count belongs to the initiating browser session, even after
      // an idle timeout. Never attach it to a later account or session.
      if (operation.kind === 'count' && knownCount) metadata.identity = knownCount.identity;
      const explicitIdentity = searchIdentities.get(metadata.searchId);
      if (
        operation.kind === 'count' && explicitIdentity &&
        sameActor(explicitIdentity, identity)
      )
        metadata.identity = explicitIdentity;
      if (metadata.phase === 'submit') {
        boundedSet(searchIdentities, metadata.searchId, identity);
        analytics.track(
          'search_submitted',
          {
            ...searchProperties({ ...data, ...config.analyticsProperties }),
            search_type: operation.name,
            search_id: metadata.searchId,
            operation_id: metadata.operationId,
          },
          { ...identity, insertId: `${metadata.searchId}:submitted` },
        );
      }
      headers[HEADERS.search] = metadata.searchId;
      headers[HEADERS.phase] = metadata.phase;
    }
    if (operation?.kind === 'chat') {
      analytics.track(
        'agent_prompt_submitted',
        {
          prompt: typeof data.message === 'string' ? data.message : undefined,
          conversation_id: data.sessionId,
          operation_id: metadata.operationId,
        },
        { ...identity, insertId: `${metadata.operationId}:prompt` },
      );
    }
    headers[HEADERS.device] = metadata.identity.deviceId;
    headers[HEADERS.session] = String(metadata.identity.sessionId);
    headers[HEADERS.operation] = metadata.operationId;
    return { ...config, headers, __analytics: metadata };
  } catch {
    return config;
  }
}

export function completeRequest(response: any): any {
  try {
    const metadata: RequestAnalytics | undefined = response.config?.__analytics;
    if (metadata?.searchId && response.data && typeof response.data === 'object')
      responseSearchIds.set(response.data, metadata.searchId);
    if (metadata?.execution && response.data && typeof response.data === 'object')
      responseExecutions.set(response.data, metadata.execution);
    if (metadata?.operation?.kind === 'search' && metadata.searchId && response.data?.countKey) {
      const { identity, operation, origin, searchId } = metadata;
      const key = `${identity.userId || 'anonymous'}:${identity.clientId || ''}:${identity.accountEpoch || ''}:${identity.deviceId}:${origin}:${
        operation.name
      }:${response.data.countKey}`;
      boundedSet(counts, key, { identity, searchId, created: Date.now() });
    }
  } catch {
    /* preserve response */
  }
  return response;
}

export function failedRequest(error: any): Promise<never> {
  try {
    const metadata: RequestAnalytics | undefined = error.config?.__analytics;
    const current = analytics.snapshot();
    // Ignore old account responses; cancellation is normal flow, not an error.
    if (
      metadata &&
      sameActor(metadata.identity, current)
    ) {
      if (error.response?.status === 401) analytics.reset();
      else if (
        !error.response &&
        !['ERR_CANCELED', 'AbortError', 'CanceledError'].includes(error.code || error.name)
      ) {
        if (metadata.execution) analytics.trackSemantic('network_failed', {
          ...metadata.execution.attribution, search_id: metadata.searchId,
        }, {
          operation: metadata.operation?.name || 'api', operation_id: metadata.operationId,
          reason: 'transport',
        }, { ...metadata.identity, executionKind: 'automatic' });
        else analytics.track(
          'network_failed',
          {
            operation: metadata.operation?.name || 'api',
            operation_id: metadata.operationId,
            search_id: metadata.searchId,
          },
          { ...metadata.identity, executionKind: 'automatic' },
        );
      }
    }
  } catch {
    /* preserve rejection */
  }
  return Promise.reject(error);
}

export const requestInterceptor = (apiBase: string) => (config: any) =>
  prepareRequest(config, apiBase);

// Used by fetch and AI SDK streaming paths. No response cloning/buffering and no
// global fetch monkey-patch; OAuth/third-party requests remain untouched.
export type AnalyticsFetch = (input: RequestInfo | URL, init?: RequestInit & { analyticsContext?: RequestAnalyticsContext }) => Promise<Response>;
export function createAnalyticsFetch(
  apiBase: string,
  transport: typeof fetch = (...args) => fetch(...args),
): AnalyticsFetch {
  return createAccessFetch(apiBase, async (input, init) => {
    const { analyticsContext, ...transportInit } = (init || {}) as RequestInit & { analyticsContext?: RequestAnalyticsContext };
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const request = typeof Request !== 'undefined' && input instanceof Request ? input : undefined;
    const originalHeaders = new Headers(request?.headers);
    new Headers(init?.headers).forEach((value, key) => originalHeaders.set(key, value));
    const requestConfig = {
        url,
        method: init?.method || request?.method || 'GET',
        data: init?.body,
        headers: Object.fromEntries(originalHeaders.entries()),
        analyticsContext,
      };
    const config = prepareRequest(requestConfig, apiBase);
    if (!config.__analytics) return transport(input, config === requestConfig ? transportInit : { ...transportInit, headers: config.headers });
    try {
      const response = await transport(input, { ...transportInit, headers: config.headers });
      if (config.__analytics.execution) responseExecutions.set(response, config.__analytics.execution);
      if (response.status === 401) {
        const current = analytics.snapshot();
        if (
          sameActor(config.__analytics.identity, current)
        )
          analytics.reset();
      }
      return response;
    } catch (error) {
      // Do not wrap or replace the original error/AbortSignal.
      try {
        await failedRequest({ config, name: (error as Error)?.name });
      } catch {
        /* telemetry only */
      }
      throw error;
    }
  });
}
